import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TitleStrategy, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DOCUMENT } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { AlertComponent, FileProcessingStatusComponent, FooterComponent, ImageComponent, LoadingComponent, ToastComponent } from '@newroom-connect/library/components';
import { ApiService, AuthService, EntityService, SessionService, TranslationService, WebsocketService, CacheModule, LocalStorageService } from '@newroom-connect/library/services';
import { HttpAuthInterceptor } from '@newroom-connect/library/interceptors';
import { CustomDomainHelper } from '@newroom-connect/library/helpers';

import { environment } from '../../../../environments/environment';
import packageInfo from '../../../../package.json';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { PageTitleStrategy } from './strategies/page-title.strategy';

// Resolve backend endpoints based on custom domain routing configuration
const backendEndpoints = CustomDomainHelper.resolveBackendEndpoints(environment);

/**
 * Factory function to add preconnect link dynamically for NgOptimizedImage.
 * This adds a preconnect link tag to the document head using the API URL from environment.
 *
 * @param document The DOM document.
 * @returns A function that adds the preconnect link when executed.
 */
export const setupPreconnectLinks = (document: Document): () => void => {
  return () => {
    const apiUrl = backendEndpoints.apiBaseUrl;

    if (apiUrl) {
      const linkElement = document.createElement('link');

      linkElement.rel = 'preconnect';

      const origin = new URL(apiUrl).origin;

      linkElement.href = origin;

      document.head.appendChild(linkElement);
    }
  };
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LoggerModule.forRoot({
      level: environment.production ? NgxLoggerLevel.OFF : NgxLoggerLevel.DEBUG
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient): TranslateHttpLoader => new TranslateHttpLoader(http, 'assets/i18n/', '.json'),
        deps: [HttpClient]
      },
      defaultLanguage: 'en'
    }),
    AppRoutingModule,
    ImageComponent,
    LoadingComponent,
    FileProcessingStatusComponent,
    AlertComponent,
    ToastComponent,
    RouterModule,
    FooterComponent,
    CacheModule.forRoot({ version: packageInfo.version }),
    // Add ServiceWorkerModule for PWA support and cache invalidation
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production || environment.development,
      // Register immediately for better offline support
      registrationStrategy: 'registerImmediately'
    })
  ],
  providers: [
    {
      provide: 'API_BASE_URL',
      useValue: backendEndpoints.apiBaseUrl
    },
    {
      provide: 'environment',
      useValue: environment
    },
    // Add preconnect link for optimized images
    {
      provide: APP_INITIALIZER,
      useFactory: setupPreconnectLinks,
      deps: [DOCUMENT],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (apiService: ApiService): () => void => () => apiService.setBaseUrl(backendEndpoints.apiBaseUrl),
      deps: [ApiService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService): () => void => () => authService.setBaseUrl(backendEndpoints.authBaseUrl),
      deps: [AuthService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (): () => void => () => EntityService.setBaseUrl(backendEndpoints.apiBaseUrl),
      deps: [EntityService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (localStorageService: LocalStorageService): () => void => () => localStorageService.setApplicationSuffix('NRCSuite'),
      deps: [LocalStorageService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (sessionService: SessionService): () => void => () => sessionService.initialize(),
      deps: [SessionService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (translationService: TranslationService): () => void => () => translationService.initialize(),
      deps: [TranslationService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (websocketService: WebsocketService): () => void => () => websocketService.initialize(backendEndpoints.websocketBaseUrl),
      deps: [WebsocketService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpAuthInterceptor,
      multi: true
    },
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategy
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
